import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious
} from "@/components/ui/pagination";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { planProxy, usePlanSnapshot } from "@/stores/plans.store";
import { SquareArrowOutUpRightIcon } from "lucide-react";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import LoadingPage from "../utils/loading.page";
import { PlanWithsubscription } from "@/types/plan.type";

export function ProductsPage() {
  const navigate = useNavigate();
  const planSnapshot = usePlanSnapshot();

  useEffect(() => {
    planProxy.fetchPlans(planSnapshot.plans.metadata.currentPage ?? 0);
  }, []);

  const columns: ColumnDef<PlanWithsubscription>[] = [
    {
      accessorKey: "plan.name",
      header: "Product",
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div
            onClick={() => navigate(`/products/${product.plan.code}`)}
            className="hover:cursor-pointer"
          >
            <div className="font-bold">{product.plan.name}</div>
            <div className="italic font-light ">{product.plan.description}</div>
          </div>
        );
      }
    },
    {
      accessorKey: "subscribed",
      header: "Status",
      cell: ({ row }) => {
        const product = row.original;
        return (
          <Badge variant={product.subscribed ? "default" : "secondary"}>
            {product.subscribed ? "Subscribed" : "Not Subscribed"}
          </Badge>
        );
      }
    },
    {
      accessorKey: "plan.productLink",
      header: "Access to platform",
      cell: ({ row }) => {
        const product = row.original;
        return (
          <Link
            to={!product.subscribed ? "" : (product.plan.productLink ?? "")}
            target="_blank"
            className={!product.subscribed ? "pointer-events-none" : ""}
          >
            <Button
              variant="outline"
              disabled={!product.subscribed}
              className="flex self-center gap-2"
            >
              <span>Access the product</span>
              <SquareArrowOutUpRightIcon />
            </Button>
          </Link>
        );
      }
    },
    {
      accessorKey: "plan.code",
      header: "Product Code"
    },
    {
      accessorKey: "plan.amount_cents",
      header: "Amount",
      cell: ({ row }) => {
        const product = row.original;
        return <span>{product.plan.amount_cents} €</span>;
      }
    }
  ];

  return (
    <>
      {planSnapshot.isLoading &&
      (!planSnapshot.plans || !planSnapshot.plans.data) ? (
        <div className="mt-8">
          <LoadingPage />
        </div>
      ) : (
        <div className="p-6 pb-16 space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Products</h1>
            <p className="text-muted-foreground">
              Browse and manage your available products
            </p>
          </div>
          <CardContent className="px-0 p-0">
            <DataTable
              columns={columns}
              data={
                Array.isArray(planSnapshot.plans?.data)
                  ? [...planSnapshot.plans.data]
                  : []
              }
              isLoading={planSnapshot.isLoading}
            />
            {planSnapshot.plans && (
              <div className="flex justify-center mt-4">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem
                      aria-disabled={
                        planSnapshot.plans.metadata.prevPage
                          ? planSnapshot.plans.metadata.prevPage <= 1
                          : true
                      }
                      className={
                        !planSnapshot.plans.metadata.prevPage ||
                        planSnapshot.plans.metadata.prevPage <= 1
                          ? "pointer-events-none opacity-50"
                          : "hover:cursor-pointer"
                      }
                      onClick={() =>
                        planProxy.fetchPlans(
                          planProxy.plans?.metadata.prevPage ?? 0
                        )
                      }
                    >
                      <PaginationPrevious />
                    </PaginationItem>

                    <PaginationItem
                      aria-disabled={
                        !planSnapshot.plans.metadata.nextPage ||
                        planSnapshot.plans.metadata.nextPage ==
                          planSnapshot.plans.metadata.totalPages
                      }
                      className={
                        !planSnapshot.plans.metadata.nextPage ||
                        planSnapshot.plans.metadata.nextPage ==
                          planSnapshot.plans.metadata.totalPages
                          ? "pointer-events-none opacity-50"
                          : "hover:cursor-pointer"
                      }
                      onClick={() =>
                        planProxy.fetchPlans(
                          planProxy.plans.metadata.nextPage ?? 0
                        )
                      }
                    >
                      <PaginationNext />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </CardContent>
        </div>
      )}
    </>
  );
}
